import type { AccessRequest, CodeInfo } from './api';

/** The admin page's own address. */
export const ACCESS_PATH = '/access';

export function isAccessPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === ACCESS_PATH;
}

const WHEN = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});
const DAY = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export function when(iso: string | null): string {
  return iso ? WHEN.format(new Date(iso)) : '';
}

export function day(iso: string): string {
  return DAY.format(new Date(iso));
}

/** "Invite request", "Access request" or the session code a lead came in with. */
export function kindLabel(r: Pick<AccessRequest, 'kind' | 'code_label'>): string {
  if (r.kind === 'session') return r.code_label ? `Session: ${r.code_label}` : 'Session';
  return r.kind === 'invite' ? 'Invite request' : 'Access request';
}

export function stateLabel(c: Pick<CodeInfo, 'state'>): string {
  return c.state === 'live' ? 'Live' : c.state === 'expired' ? 'Expired' : 'Ended';
}

/** A request someone is still waiting on: the admin can send a code or decline. */
export function waiting(r: AccessRequest): boolean {
  return r.state === 'pending' && r.kind !== 'session';
}

function cell(value: string | number | null): string {
  const text = value === null ? '' : String(value);
  // Quote everything; neutralise a leading formula character so a spreadsheet shows it as text.
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

/** Every request and lead as CSV, for the follow-up: one row each, newest first. */
export function leadsCsv(rows: readonly AccessRequest[]): string {
  const head = ['When', 'Name', 'Email', 'Company', 'Phone', 'Note', 'How', 'State'];
  const body = rows.map((r) =>
    [r.created_at, r.name, r.email, r.organisation, r.phone, r.reason, kindLabel(r), r.state].map(cell).join(','),
  );
  return [head.map(cell).join(','), ...body].join('\r\n') + '\r\n';
}

/** The message to send someone with their invite code (no em dashes, no "we"). */
export function inviteMessage(opts: { title: string; origin: string; code: string; expiresAt: string }): string {
  return [
    `Your invite code for ${opts.title}: ${opts.code}`,
    `Open ${opts.origin} and enter it under Invite code. It works until ${day(opts.expiresAt)}.`,
  ].join('\n');
}

/** What is wrong with a new password, or '' (at least 12 characters, typed the same twice). */
export function passwordProblem(password: string, repeat: string): string {
  if (password.length < 12) return 'Use at least 12 characters.';
  if (password !== repeat) return 'The two passwords differ.';
  return '';
}

/** The authenticator key in groups of four, as authenticator apps show it. */
export function groupKey(key: string): string {
  return key.match(/.{1,4}/g)?.join(' ') ?? '';
}

/** A QR code's SVG as an image address (the page's policy allows data: images). */
export function svgImage(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
